import { z } from 'zod'
import { isoDate, isoDateTime, ianaTimeZone, uuid } from './common.js'
import { phoneE164 } from './participants.js'

/**
 * One thing on a day.
 *
 * Replaces the old `place` / `reservation` pair. Every field is optional except
 * the title, because the split between "things with a time" and "things with an
 * address" never matched anything real: a hotel has both, a museum visit has
 * both, and a note has neither.
 */

export const itemKind = z.enum([
  'flight',
  'train',
  'transfer',
  'car',
  'hotel',
  'restaurant',
  'cafe',
  'bar',
  'museum',
  'activity',
  'shop',
  'note',
  'other',
])
export type ItemKind = z.infer<typeof itemKind>

/** Latitude/longitude entered by hand are first-class — a geocoder must never block. */
export const latitude = z.number().min(-90).max(90)
export const longitude = z.number().min(-180).max(180)

/**
 * The longest a note may be, on an item or on a day.
 *
 * Named rather than repeated, because anything that WRITES a note without going
 * through these schemas has to honour the same ceiling — inserting a snippet
 * does, and a note grown past what the contract accepts is a note Ana can no
 * longer edit through the ordinary form.
 */
export const MAX_NOTES_LENGTH = 20_000

const itemFields = {
  kind: itemKind.default('other'),
  title: z.string().trim().min(1).max(200),
  /**
   * The clock reading, in `timezone` or the trip's. Sent as an instant because
   * that is what it is; the client converts through `wallClockToInstant` so the
   * hour Ana types is the hour in the city she names. See PATTERNS §3b.
   */
  startsAt: isoDateTime.nullish(),
  endsAt: isoDateTime.nullish(),
  timezone: ianaTimeZone.nullish(),
  /**
   * Where it ENDS, when that is somewhere else — an arriving flight, an
   * overnight train. Null means "the same place it started", which is true of
   * everything that does not cross a boundary.
   */
  endTimezone: ianaTimeZone.nullish(),
  address: z.string().max(500).nullish(),
  addressLocal: z.string().max(500).nullish(),
  lat: latitude.nullish(),
  lng: longitude.nullish(),
  website: z.url().max(2000).nullish(),
  phone: phoneE164.nullish(),
  confirmationRef: z.string().max(200).nullish(),
  notes: z.string().max(MAX_NOTES_LENGTH).nullish(),
  dayId: uuid.nullish(),
  sortOrder: z.int().min(0).max(100_000).optional(),
}

export const createItemRequest = z.object(itemFields)
export type CreateItemRequest = z.infer<typeof createItemRequest>

export const updateItemRequest = z.object(itemFields).partial()
export type UpdateItemRequest = z.infer<typeof updateItemRequest>

export const item = z.object({
  id: uuid,
  tripId: uuid,
  dayId: uuid.nullable(),
  kind: itemKind,
  title: z.string(),
  startsAt: isoDateTime.nullable(),
  endsAt: isoDateTime.nullable(),
  timezone: z.string().nullable(),
  endTimezone: z.string().nullable(),
  address: z.string().nullable(),
  addressLocal: z.string().nullable(),
  lat: latitude.nullable(),
  lng: longitude.nullable(),
  website: z.string().nullable(),
  phone: z.string().nullable(),
  confirmationRef: z.string().nullable(),
  notes: z.string().nullable(),
  sortOrder: z.int(),
  /**
   * When Ana first typed this, and when she last touched it.
   *
   * Carried on the traveller's payload as well as the advisor's, because this
   * pair IS the "what changed since you last looked" feature — see
   * `time/changes.ts`. Both are the DATABASE's clock, set by a column default
   * and by `app.touch_updated_at()`, which is what makes them comparable
   * against `SharePlan.readAt`. Nothing in this product ever compares them
   * against a browser's idea of the time.
   */
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
})
export type Item = z.infer<typeof item>

// ---------------------------------------------------------------------------
// Days and the assembled plan
// ---------------------------------------------------------------------------

export const updateDayRequest = z.object({
  /** What the day is called: "Versalhes". Blank shows the date instead. */
  title: z.string().trim().max(120).nullish(),
  /**
   * A short line about the day, not the day itself.
   *
   * It used to be the only place a day could be described, so it grew into a
   * wall of text that carried addresses, times and phone numbers the product
   * should have held as fields. Those live in items now; this is the sentence
   * that goes above them, and it is usually blank.
   */
  notes: z.string().max(MAX_NOTES_LENGTH).nullish(),
})
export type UpdateDayRequest = z.infer<typeof updateDayRequest>

export const tripDay = z.object({
  id: uuid,
  tripId: uuid,
  date: isoDate,
  title: z.string().nullable(),
  notes: z.string().nullable(),
  /**
   * As on an item, and for the same reason. A day's own text changes — Ana
   * renames Tuesday, or rewrites the paragraph above its bookings — and that is
   * a change the traveller should be shown, not only the entries beneath it.
   *
   * `createdAt` on a day is not noise either: days are generated for the trip's
   * whole range at once, so a day that appears later is the trip getting longer.
   */
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
})
export type TripDay = z.infer<typeof tripDay>

/** One day, with everything on it, in the order it happens. */
export const planDay = tripDay.extend({
  items: z.array(item),
})
export type PlanDay = z.infer<typeof planDay>

/** On the trip but not yet on a day. Rendered after the days, never dropped. */
export const planLoose = z.object({
  items: z.array(item),
})
export type PlanLoose = z.infer<typeof planLoose>

/** Copying one day of one trip onto a day of another. */
export const copyDayRequest = z.object({
  sourceDayId: uuid,
})
export type CopyDayRequest = z.infer<typeof copyDayRequest>

/** A day of another trip, as offered in the "copy a day" picker. */
export const dayOption = z.object({
  id: uuid,
  tripId: uuid,
  tripTitle: z.string(),
  date: isoDate,
  itemCount: z.int(),
  hasNotes: z.boolean(),
})
export type DayOption = z.infer<typeof dayOption>
